import TrendingIcon from '@icons/trend.svg';
import UseCasesIcon from '@icons/UseCasesIcon.svg';
import RetentionIcon from '@icons/RetentionIcon.svg';
import type { UseCasesWhyHowBlock } from '@components/widgets';

export const FMOS_USE_CASES_WHY_HOW_SECTION_TITLE =
    'Why Predictable Benefits for Field Marketing Organizations';

export const FMOS_USE_CASES_WHY_HOW_BLOCKS: UseCasesWhyHowBlock[] = [
    {
        pillNavLabel: 'Broker Protection',
        title: 'Protecting the "Broker of Record" (BOR) & Downline Commissions',
        description:
            'Traditional ICHRA platforms often aim to "own" the relationship with the employer, frequently sidelining the agent or reducing them to a mere "referral partner" with diminishing overrides.',
        cards: [
            {
                iconSrc: UseCasesIcon,
                title: 'The Use Case',
                description:
                    'An FMO provides its agents with a white-labeled version of PB. The agent remains the primary consultant and the BOR on all individual policies selected by employees.',
            },
            {
                iconSrc: TrendingIcon,
                title: 'Growth Impact',
                description:
                    'FMOs can recruit high-producing health agents by offering a "safe haven" where their books of business are technologically protected from direct-to-consumer platforms.',
            },
            {
                iconSrc: RetentionIcon,
                title: 'Retention Impact',
                description:
                    'Agents are less likely to leave an FMO that provides the essential infrastructure (the "tech stack") required to compete in the modern defined-contribution landscape.',
            },
        ],
    },
    {
        pillNavLabel: 'Lead Conversion',
        title: 'Turning "Dead Leads" into Revenue via No-Participation Hurdles',
        description:
            'Agents often lose groups because they cannot meet the participation requirements of traditional small-group carriers—often due to employees being on a spouse’s plan or Medicaid.',
        cards: [
            {
                iconSrc: UseCasesIcon,
                title: 'The Use Case',
                description:
                    'When a group case "fails" participation, the FMO agent immediately pivots to the PB platform. Because ICHRA has no minimum participation requirements, the agent closes the deal regardless of enrollment volume.',
            },
            {
                iconSrc: TrendingIcon,
                title: 'Growth Impact',
                description:
                    'Dramatically increases the "close ratio" for the FMO’s entire agent force by providing a viable solution for every single prospect, regardless of health status or participation.',
            },
        ],
    },
    {
        pillNavLabel: 'Compliance at Scale',
        title: 'Scalable Compliance for "Niche" or High-Churn Industries',
        description:'FMOs often serve agents working in industries with high turnover (e.g., hospitality, construction, or retail) where managing traditional group administration is a nightmare for a solo agent.',
        cards: [
            {
                iconSrc: UseCasesIcon,
                title: 'The Use Case',
                description:
                    'The PB platform automates the complex ACA affordability calculations and notice requirements. The FMO provides this "compliance-as-a-service" to their agents, removing the administrative burden of servicing high-churn clients.',
            },
            {
                iconSrc: RetentionIcon,
                title: 'Retention Impact',
                description:
                    'By simplifying the "service" side of the business, the FMO allows the agent to focus purely on selling, making the FMO an indispensable partner in the agent\'s daily workflow.',
            },
        ],
    },
    {
        pillNavLabel: 'Geographic Expansion',
        title: 'Strategic "Class-Based" Geographic Expansion',
        description:
            'FMOs looking to expand nationally often struggle with localized carrier networks and varying state regulations.',
        cards: [
            {
                iconSrc: UseCasesIcon,
                title: 'The Use Case',
                description:
                    'Instead of an FMO needing to secure 50 different small-group appointments for an agent, the agent uses the PB platform to access the individual exchange in all 50 states.',
            },
            {
                iconSrc: TrendingIcon,
                title: 'Growth Impact',
                description:
                    'Allows an FMO based in one region to instantly empower its agents to write business nationwide without the friction of traditional group underwriting or regional network limitations.',
            },
        ],
    },
    {
        pillNavLabel: 'White-Label Branding',
        title: 'Brand Authority via White-Labeling',
        description:
            'In a crowded market, FMOs struggle to differentiate themselves from one another. Most look like "middlemen" passing through carrier products.',
        cards: [
            {
                iconSrc: UseCasesIcon,
                title: 'The Use Case',
                description:
                    'The FMO brands the Predictable Benefits platform as its own proprietary technology (e.g., "FMO-Elite Health Portal").',
            },
            {
                iconSrc: RetentionIcon,
                title: 'Retention Impact',
                description:
                    'The FMO shifts from being a "distributor" to a "technology provider." When an agent logs into a portal with the FMO’s logo every day to manage their clients, the FMO’s brand becomes synonymous with the agents success.',
            },
        ],
    },
    {
        pillNavLabel: 'Data & Cross-Sell',
        title: 'Cross-Sell Infrastructure & Data Ownership',
        description:
            'FMOs thrive on the ability to see what is happening across their entire downline to suggest cross-sell opportunities (Life, DI, Medicare).',
        cards: [
            {
                iconSrc: UseCasesIcon,
                title: 'The Use Case',
                description:
                    'Because the FMO owns the white-labeled platform, they have a birds-eye view of the census data across all groups enrolled.',
            },
            {
                iconSrc: TrendingIcon,
                title: 'Growth Impact',
                description:
                    'The FMO can identify "aging-in" employees for Medicare transitions or high-income executives for supplemental life products, feeding these warm leads back to their agents.',
            },
        ],
    },
];
